// src/menu/SAL/ComCreateUi/UI_Layout_V2.tsx

import React from "react";
// 모든 컴포넌트 import (경로는 프로젝트 설정에 맞게 조정하세요)
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnButton from "@shared/components/SnButton";
import SnNumPadButton from "@shared/components/SnNumPadButton";
import SnResizeBar from "@shared/components/SnResizeBar";
// JSON 설정 파일 import
import SearchPanel from "@widgets/SearchPanel";
import SnTab from "@shared/components/SnTab";
import SnTabHeader from "@shared/components/SnTabHeader";
import SnForm from "@widgets/SnForm";
import SnFileUploadBox from "@shared/components/SnFileUploadBox";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import { globalMenu, onSearchStore } from "@shared/libs/Global";
import {
  createChatScreenDataFromGridRefs,
  getVisibleChatGridIds,
} from "@shared/utils/ChatScreenDataUtils";

// 1. 컴포넌트 맵 정의
const ComponentMap: { [key: string]: any } = {
  SnGrid: SnGrid,
  SnButton: SnButton,
  SnNumPadButton: SnNumPadButton,
  SnResizeBar: SnResizeBar,
  SearchPanel: SearchPanel,
  SnForm: SnForm,
  SnTab: SnTab,
  SnTabHeader: SnTabHeader,
  SnFileUploadBox: SnFileUploadBox,
  div: "div", // 일반 div 태그 지원
  SnSearchBtn,
};

// 2. Props 및 Config 타입 정의 (간소화)
interface DynamicProps {
  [key: string]: any;
}

interface SearchControl {
  cd_menu?: string;
}

interface ComCreateUi_UiProps {
  dynamicProps: DynamicProps;
  layoutConfig: {
    layout: UIConfig[];
  };
}
export interface UIConfig {
  type: string;
  id?: string;
  className?: string;
  style?: React.CSSProperties;
  props?: { [key: string]: any };
  header?: UIConfig[];
  body?: UIConfig[];
  children?: UIConfig[];
}

/**
 * JSON 설정에 따라 UI를 동적으로 렌더링하는 재귀 함수
 */
const renderComponent = (
  config: UIConfig,
  dynamicProps: DynamicProps
): React.ReactNode => {
  const Component = ComponentMap[config.type];
  if (!Component) {
    console.error(`Unknown component type: ${config.type}`);
    return null;
  }

  // Props 조합 (JSON 정적 Props + 동적 Props/Handlers)
  let componentProps: { [key: string]: any } = {
    ...config.props,
    className: config.className,
    style: config.style,
  };

  // "_key"를 사용하여 동적 Props 주입
  for (const key in config.props) {
    if (key.endsWith("_key")) {
      const dynamicKey = config.props[key];
      const propName = key.substring(0, key.length - 4);
      componentProps[propName] = dynamicProps[dynamicKey];
    }
  }

  // Ref 처리 (JSON에서 ref 키를 찾아 Ref 객체 주입)
  if (config.props?.ref) {
    componentProps.ref = dynamicProps[config.props.ref];
  }

  // Events 객체 처리
  if (config.props?.events_key) {
    componentProps.events = dynamicProps[config.props.events_key];
  }

  // Header/Children 요소 처리 (재귀 호출)
  let children: React.ReactNode[] = [];
  if (config.children) {
    children = config.children.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }
  if (Array.isArray(config.header)) {
    componentProps.header = config.header.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }

  if (Array.isArray(config.body)) {
    componentProps.body = config.body.map((child) =>
      renderComponent(child, dynamicProps)
    );
  }

  return <Component {...componentProps}>{children}</Component>;
};

interface RenderItemProps {
  config: UIConfig;
  dynamicProps: DynamicProps;
}

const RenderItem: React.FC<RenderItemProps> = React.memo(
  ({ config, dynamicProps }) => {
    const Component = ComponentMap[config.type];

    if (!Component) return null;

    // ✅ props 생성
    const mergedProps: any = {
      ...config.props,
      className: config.className,
      //style: config.style,
    };

    /***************************************
     * 1. Ref 자동 연결 (string 절대 금지)
     ***************************************/
    if (config.props?.ref_key) {
      const key = config.props.ref_key;

      if (config.type === "SnGrid" && dynamicProps.gridRefs) {
        if (!dynamicProps.gridRefs.current[key]) {
          dynamicProps.gridRefs.current[key] = React.createRef();
        }
        mergedProps.ref = dynamicProps.gridRefs.current[key];
      }

      if (config.type === "SnForm" && dynamicProps.formRefs) {
        if (!dynamicProps.formRefs.current[key]) {
          dynamicProps.formRefs.current[key] = React.createRef();
        }
        mergedProps.ref = dynamicProps.formRefs.current[key];
      }

      if (key === "searchRef" && dynamicProps.searchRef) {
        mergedProps.ref = dynamicProps.searchRef;
      }
      if (key === "searchRef" && dynamicProps.searchRef) {
        mergedProps.ref = dynamicProps.searchRef;
      }
    }

    if (typeof mergedProps.ref === "string") {
      delete mergedProps.ref;
    }

    if (config.type === "SearchPanel" && config.props?.search_key) {
      const searchKey = config.props.search_key;
      const searchData = dynamicProps[searchKey];
      if (searchData) {
        mergedProps.data = searchData.data;
        mergedProps.controls = searchData.controls;
        mergedProps.onValueChanged = searchData.onValueChanged;
        if (searchData.popupRef) {
          mergedProps.popupRef = dynamicProps.popupRef;
        }
      }
    }

    if (
      (config.type === "SnButton" || config.type === "SnNumPadButton") &&
      config.id
    ) {
      const buttonData = dynamicProps.buttons?.[config.id];

      if (buttonData) {
        Object.assign(mergedProps, buttonData);

        if (buttonData.events) {
          mergedProps.events = buttonData.events;
        }
      }
    }

    /***************************************
     * 2. Grid 데이터 바인딩
     ***************************************/
    if (
      (config.type === "SnGrid" || config.type === "SnForm") &&
      config.props?.etcProps
    ) {
      Object.assign(mergedProps, config.props.etcProps);
    }

    // ✅ 여기 바로 아래에 추가
    if (mergedProps.header && typeof mergedProps.header === "object") {
      mergedProps.header = (
        <RenderItem config={mergedProps.header} dynamicProps={dynamicProps} />
      );
    }
    if (config.type === "SnGrid" && config.props?.grid_key) {
      const gridKey = config.props.grid_key;
      const gridData = dynamicProps.grids?.[gridKey];

      if (gridData) {
        mergedProps.columnConfig = gridData.columns;
        mergedProps.rowData = gridData.dataSource;
        mergedProps.events = gridData.events;
        // grid 동적 설정(etcProps)을 SnGrid에 전달
        // 26.09.07 kdy 레이아웃 JSON 의 etcProps(title 등)를 통째로 덮어쓰면 SnTab 헤더가 사라지므로 합쳐서 전달한다.
        if (gridData.etcProps) {
          mergedProps.etcProps = {
            ...(mergedProps.etcProps ?? {}),
            ...gridData.etcProps,
          };
        }
        if (gridData.headerInfo) {
          mergedProps.headerInfo = gridData.headerInfo;
        }
        if (gridData.filter) {
          const { refs, columns } = gridData.filter;

          mergedProps.filter = {
            columns,
            refs: refs
              ?.map((key: string) => dynamicProps.gridRefs.current[key])
              .filter(Boolean), // 아직 생성 안된 ref 제거
          };
        }
      }
    }

    if (config.type === "SnForm" && config.props?.form_key) {
      const formKey = config.props.form_key;
      const formData = dynamicProps.forms?.[formKey];

      if (formData) {
        mergedProps.controls = formData.controls;
        mergedProps.data = formData.data;
        mergedProps.events = formData.events;
        if (formData.headerInfo) {
          mergedProps.headerInfo = formData.headerInfo;
        }
        if (formData.gridref) {
          mergedProps.gridref = dynamicProps.gridRefs.current[formData.gridref];
        }
        if (formData.filter) {
          const { refs, columns } = formData.filter;

          mergedProps.filter = {
            columns,
            refs: refs
              ?.map((key: string) => dynamicProps.gridRefs.current[key])
              .filter(Boolean), // 아직 생성 안된 ref 제거
          };
        }
        if (formData.popupRef) {
          mergedProps.popupRef = formData.popupRef;
        }
      }
    }

    if (config.type === "SnTabHeader" && config.props?.header_key) {
      const headerKey = config.props.header_key;
      const headerData = dynamicProps.headers?.[headerKey];
      if (headerData) {
        mergedProps.onBtnAddClick = headerData.onBtnAddClick;
        mergedProps.onBtnDeleteClick = headerData.onBtnDeleteClick;
        mergedProps.headerInfo = headerData.headerInfo;
        mergedProps.onHeaderValueChanged = headerData.onHeaderValueChanged;
        mergedProps.onBtnEtcClick = headerData.onBtnEtcClick;
      }
    }

    if (config.type === "SnFileUploadBox" && config.props?.fileUplaod_key) {
      const fileUplaodKey = config.props.fileUplaod_key;
      const fileUplaodData = dynamicProps.fileUpload?.[fileUplaodKey];
      if (fileUplaodData) {
        mergedProps.selectedDocu = fileUplaodData.selectedDocu;
        mergedProps.selectedLineDocu = fileUplaodData.selectedLineDocu;
        mergedProps.selectedDtDocu = fileUplaodData.selectedDtDocu;
      }
    }

    /***************************************
     * 3. 이벤트 자동 매핑
     ***************************************/
    if (config.props?.onClick_key) {
      mergedProps.onClick = dynamicProps[config.props.onClick_key];
    }

    if (config.props?.events_key) {
      mergedProps.events = dynamicProps[config.props.events_key];
    }

    const children = config.children?.map((child, index) => {
      return (
        <RenderItem
          key={child.id ?? index}
          config={child}
          dynamicProps={dynamicProps}
        />
      );
    });

    if (config.type === "SnTab") {
      if (Array.isArray(config.body)) {
        mergedProps.body = config.body.map((child: UIConfig) => (
          <RenderItem
            key={child.id}
            config={child}
            dynamicProps={dynamicProps}
          />
        ));
      }

      if (Array.isArray(config.header)) {
        mergedProps.header = config.header.map((child: UIConfig) => (
          <RenderItem
            key={child.id}
            config={child}
            dynamicProps={dynamicProps}
          />
        ));
      }

      return <Component {...mergedProps} />;
    }

    return <Component {...mergedProps}>{children}</Component>;
  }
);

const ComCreateUi = React.memo(
  ({ dynamicProps, layoutConfig }: ComCreateUi_UiProps) => {
    React.useEffect(() => {
      const menuCode = getMenuCode(dynamicProps);
      if (!menuCode || !dynamicProps.gridRefs) {
        return;
      }
      const chatGridIds = getVisibleChatGridIds(layoutConfig.layout);

      onSearchStore.getState().registerChatScreenDataProvider(menuCode, {
        getScreenData: () =>
          createChatScreenDataFromGridRefs(dynamicProps.gridRefs, chatGridIds),
      });

      return () => {
        onSearchStore.getState().unregisterChatScreenDataProvider(menuCode);
      };
    }, [dynamicProps, layoutConfig.layout]);

    return (
      <div className="h-full flex flex-col">
        {layoutConfig.layout.map((config, index) => {
          return (
            <RenderItem
              key={config.id ?? index}
              config={config}
              dynamicProps={dynamicProps}
            />
          );
        })}
      </div>
    );
  }
);

function getMenuCode(dynamicProps: DynamicProps): string | undefined {
  return (
    getSearchControls(dynamicProps).find((control) => control.cd_menu)
      ?.cd_menu || globalMenu.getState().activeMenuInfo?.cd_menu
  );
}

function getSearchControls(dynamicProps: DynamicProps): SearchControl[] {
  const controls = dynamicProps.search?.controls;
  return Array.isArray(controls) ? controls : [];
}

export default ComCreateUi;
